import { z } from 'zod';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../../../stored-json-contract.types';
import type {
    CloverOAuthCredentials,
    CloverOAuthGrantBindingCredentials,
    DraftCloverOAuthGrantBindingCredentials,
    CloverOAuthCredentialsPublic,
    CloverOAuthCredentialsSecret,
    CloverResolvedCredentials,
    CloverResolvedOAuthCredentials,
    CloverCredentials,
    CloverPersistedOAuthCredentialsV2,
    CloverPersistedOAuthCredentialsV2GrantBinding,
    CloverPersistedOAuthCredentialsV2Manual,
    DraftCloverOAuthCredentials,
    CloverStoredOAuthCredentials,
    CloverResolvedOAuthGrantBindingCredentials,
    DraftCloverCredentials,
} from '../../../types/ipg/clover/clover-credential.types';
import { CloverAuthorizationMode } from '../../../types/ipg/clover/clover-credential.types';

export const CloverOAuthCredentialsPublicSchema: z.ZodType<CloverOAuthCredentialsPublic> = z
    .object({
        merchantId: z.string().min(1),
    })
    .strict();

export const CloverOAuthCredentialsSecretSchema: z.ZodType<CloverOAuthCredentialsSecret> = z
    .object({
        accessToken: z.string().min(1),
    })
    .strict();

export const CloverOAuthCredentialsSchema: z.ZodType<CloverOAuthCredentials> = z
    .object({
        merchantId: z.string().min(1),
        accessToken: z.string().min(1),
    })
    .strict();

export const CloverLegacyOAuthCredentialsSchema: z.ZodType<CloverOAuthCredentials> =
    CloverOAuthCredentialsSchema;

export const DraftCloverOAuthCredentialsSchema: z.ZodType<DraftCloverOAuthCredentials> = z
    .object({
        merchantId: z.string().min(1).optional(),
        accessToken: z.string().min(1).optional(),
    })
    .strict()
    .refine((value) => value.merchantId !== undefined || value.accessToken !== undefined, {
        message: 'At least one credential field is required',
    });

export const CloverOAuthGrantBindingCredentialsSchema: z.ZodType<CloverOAuthGrantBindingCredentials> =
    z
        .object({
            merchantId: z.string().min(1),
        })
        .strict();

export const DraftCloverOAuthGrantBindingCredentialsSchema: z.ZodType<DraftCloverOAuthGrantBindingCredentials> =
    z
        .object({
            merchantId: z.string().min(1).optional(),
        })
        .strict();

export const DraftCloverOAuthGrantBindingPublicCredentialsSchema: z.ZodType<DraftCloverOAuthGrantBindingCredentials> =
    DraftCloverOAuthGrantBindingCredentialsSchema;

export const CloverPersistedOAuthCredentialsV2ManualSchema: z.ZodType<CloverPersistedOAuthCredentialsV2Manual> =
    z
        .object({
            credential_payload_version: z.literal(2),
            authorization_source: z.literal('manual'),
            merchantId: z.string().min(1),
            accessToken: z.string().min(1),
        })
        .strict();

export const CloverPersistedOAuthCredentialsV2GrantBindingSchema: z.ZodType<CloverPersistedOAuthCredentialsV2GrantBinding> =
    z
        .object({
            credential_payload_version: z.literal(2),
            authorization_source: z.literal('grant'),
            merchantId: z.string().min(1),
        })
        .strict();

export const CloverPersistedOAuthCredentialsV2Schema: z.ZodType<CloverPersistedOAuthCredentialsV2> =
    z.union([
        CloverPersistedOAuthCredentialsV2ManualSchema,
        CloverPersistedOAuthCredentialsV2GrantBindingSchema,
    ]);

export const CloverStoredOAuthCredentialsSchema: z.ZodType<CloverStoredOAuthCredentials> = z.union([
    CloverOAuthCredentialsSchema,
    CloverPersistedOAuthCredentialsV2Schema,
]);

export const CloverOAuthCredentialStorageContract = defineStoredJsonContract({
    contractName: 'CloverOAuthCredentialStorage',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'CloverOAuthCredentialsSchema',
            schema: CloverOAuthCredentialsSchema,
            guards: [
                {
                    kind: 'json_marker_absent',
                    markerPath: ['credential_payload_version'],
                },
            ],
            normalize: (credentials: CloverOAuthCredentials): DraftCloverOAuthCredentials =>
                credentials,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
        v2: {
            schemaName: 'CloverPersistedOAuthCredentialsV2Schema',
            schema: CloverPersistedOAuthCredentialsV2Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['credential_payload_version'],
                    markerValue: 2,
                },
            ],
            normalize: (
                credentials: CloverPersistedOAuthCredentialsV2
            ): DraftCloverOAuthCredentials =>
                credentials.authorization_source === 'manual'
                    ? {
                          merchantId: credentials.merchantId,
                          accessToken: credentials.accessToken,
                      }
                    : { merchantId: credentials.merchantId },
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v2',
});

export const CloverPersistedOAuthCredentialsParser: z.ZodType<DraftCloverOAuthCredentials> =
    buildStoredJsonValueParser(CloverOAuthCredentialStorageContract);

export function serializeDraftCloverOAuthCredentials(
    credentials: DraftCloverOAuthCredentials
): CloverPersistedOAuthCredentialsV2 {
    const parsedCredentials = DraftCloverOAuthCredentialsSchema.parse(credentials);
    if (parsedCredentials.merchantId === undefined) {
        throw new Error('Clover OAuth credentials require merchantId for persistence');
    }
    return parsedCredentials.accessToken === undefined
        ? {
              credential_payload_version: 2,
              authorization_source: 'grant',
              merchantId: parsedCredentials.merchantId,
          }
        : {
              credential_payload_version: 2,
              authorization_source: 'manual',
              merchantId: parsedCredentials.merchantId,
              accessToken: parsedCredentials.accessToken,
          };
}

export const DraftCloverOAuthPublicCredentialsSchema: z.ZodType<
    Pick<DraftCloverOAuthCredentials, 'merchantId'>
> = z
    .object({
        merchantId: z.string().min(1).optional(),
    })
    .strict();

export const DraftCloverCredentialsSchema: z.ZodType<DraftCloverCredentials> =
    DraftCloverOAuthCredentialsSchema;

export const CloverResolvedOAuthCredentialsSchema: z.ZodType<CloverResolvedOAuthCredentials> = z
    .object({
        authorizationMode: z.literal(CloverAuthorizationMode.OAUTH),
        merchantId: z.string().min(1),
        accessToken: z.string().min(1),
    })
    .strict();

export const CloverResolvedOAuthGrantBindingCredentialsSchema: z.ZodType<CloverResolvedOAuthGrantBindingCredentials> =
    z
        .object({
            authorizationMode: z.literal(CloverAuthorizationMode.OAUTH_GRANT_BINDING),
            merchantId: z.string().min(1),
        })
        .strict();

export const CloverResolvedCredentialsSchema: z.ZodType<CloverResolvedCredentials> = z.union([
    CloverResolvedOAuthCredentialsSchema,
    CloverResolvedOAuthGrantBindingCredentialsSchema,
]);

export const CloverCredentialsSchema: z.ZodType<CloverCredentials> =
    CloverResolvedCredentialsSchema;

export const CloverStoredOAuthToResolvedCredentialsSchema: z.ZodType<CloverResolvedCredentials> =
    CloverStoredOAuthCredentialsSchema.transform((credentials) => {
        if ('credential_payload_version' in credentials) {
            return credentials.authorization_source === 'manual'
                ? {
                      authorizationMode: CloverAuthorizationMode.OAUTH,
                      merchantId: credentials.merchantId,
                      accessToken: credentials.accessToken,
                  }
                : {
                      authorizationMode: CloverAuthorizationMode.OAUTH_GRANT_BINDING,
                      merchantId: credentials.merchantId,
                  };
        }
        return {
            authorizationMode: CloverAuthorizationMode.OAUTH,
            ...credentials,
        };
    });

export const CloverStoredOAuthGrantBindingToResolvedCredentialsSchema: z.ZodType<CloverResolvedOAuthGrantBindingCredentials> =
    CloverPersistedOAuthCredentialsV2GrantBindingSchema.transform((credentials) => ({
        authorizationMode: CloverAuthorizationMode.OAUTH_GRANT_BINDING,
        merchantId: credentials.merchantId,
    }));
